import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { supabase } from './supabase'
import { setTimezone, setLocale } from './dateUtils'
import { narrowCountry, localeForCountry } from './country'

/**
 * @typedef {Object} BrandingContextValue
 * @property {string} org_name
 * @property {string} org_tagline
 * @property {string} org_address
 * @property {string} org_contact_name
 * @property {string} org_contact_title
 * @property {string} org_email
 * @property {string} org_phone
 * @property {string} org_domain
 * @property {string} logo_url
 * @property {string} primary_color
 * @property {string} secondary_color
 * @property {string} accent_color
 * @property {string} email_from_address
 * @property {string} email_from_name
 * @property {string} timezone
 * @property {string} currency
 * @property {'ca' | 'us'} country
 * @property {string} legal_entity_name
 * @property {string} industry_type
 * @property {string} business_number
 * @property {string} gst_hst_number
 * @property {string} provincial_tax_number
 * @property {string} wcb_account_number
 * @property {string} incorporation_number
 * @property {string} ein
 * @property {string} state_sales_tax_id
 * @property {string} state_contractor_license
 * @property {string} workers_comp_policy
 * @property {string} business_registration_number
 * @property {unknown} tax_config
 * @property {Record<string, string> | undefined} [terminology]
 * @property {boolean} loading
 * @property {(updates: Record<string, unknown>) => Promise<{ data: unknown, error: unknown }>} updateBranding
 * @property {() => Promise<void>} reload
 */

/** @type {React.Context<BrandingContextValue>} */
const BrandingContext = createContext(/** @type {BrandingContextValue} */ ({
  .../** @type {any} */ ({}),
  loading: true,
  updateBranding: async () => ({ data: null, error: null }),
  reload: async () => {},
}))

/**
 * @typedef {{
 *   org_name: string, org_tagline: string, org_address: string,
 *   org_contact_name: string, org_contact_title: string, org_email: string,
 *   org_phone: string, org_domain: string, logo_url: string,
 *   primary_color: string, secondary_color: string, accent_color: string,
 *   email_from_address: string, email_from_name: string,
 *   timezone: string, currency: string, country: string,
 *   legal_entity_name: string, industry_type: string,
 *   business_number: string, gst_hst_number: string, provincial_tax_number: string,
 *   wcb_account_number: string, incorporation_number: string,
 *   ein: string, state_sales_tax_id: string, state_contractor_license: string,
 *   workers_comp_policy: string, business_registration_number: string,
 *   tax_config: unknown,
 * }} BrandingState
 */

/** @type {BrandingState} */
const DEFAULTS = {
  org_name: 'CrewOS',
  org_tagline: 'Crew Operations Platform',
  org_address: '',
  org_contact_name: '',
  org_contact_title: '',
  org_email: '',
  org_phone: '',
  org_domain: '',
  logo_url: '/logo.svg',
  primary_color: '#1a2e1a',
  secondary_color: '#7aa37a',
  accent_color: '#16a34a',
  email_from_address: '',
  email_from_name: '',
  timezone: 'America/Vancouver',
  currency: 'CAD',
  country: 'ca',
  legal_entity_name: '',
  industry_type: '',
  business_number: '',
  gst_hst_number: '',
  provincial_tax_number: '',
  wcb_account_number: '',
  incorporation_number: '',
  ein: '',
  state_sales_tax_id: '',
  state_contractor_license: '',
  workers_comp_policy: '',
  business_registration_number: '',
  tax_config: null,
}

/**
 * @param {{ children: React.ReactNode }} props
 */
export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS)
  const [loading, setLoading] = useState(true)
  /** @type {React.MutableRefObject<string | null>} */
  const accountIdRef = useRef(/** @type {string | null} */ (null))

  useEffect(() => {
    loadBranding()
  }, [])

  /**
   * Coerce DB nulls to empty strings so the merged branding object stays
   * non-null-string shape (matches DEFAULTS + BrandingContextValue typedef).
   * Supabase typed row has nullable columns; consumers expect `string` so
   * patterns like `branding.org_name || 'CrewOS'` work without null checks.
   * @param {Record<string, unknown> | null | undefined} row
   * @returns {Record<string, string>}
   */
  function coerceRow(row) {
    /** @type {Record<string, string>} */
    const out = {}
    if (!row) return out
    for (const [k, v] of Object.entries(row)) {
      if (typeof v === 'string') out[k] = v
      else if (v === null || v === undefined) out[k] = ''
      // non-string non-null values (booleans, numbers, JSONB) are ignored —
      // branding context only uses string fields
    }
    return out
  }

  async function loadBranding() {
    try {
      // 1. Resolve account_id from the authenticated user's profile first, so
      //    updateBranding has a valid account_id even when no org_settings row
      //    exists yet (first-save lazy-create path — Cure Run 17 D14).
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('account_id')
          .eq('id', user.id)
          .maybeSingle()
        if (profile?.account_id) accountIdRef.current = /** @type {string} */ (profile.account_id)
      }
      // 2. Try to load org_settings for this account. Missing row is OK —
      //    we keep DEFAULTS and let the first save upsert create the row.
      const { data } = await supabase
        .from('org_settings')
        .select('*')
        .limit(1)
        .maybeSingle()
      if (data) {
        if (data.account_id) accountIdRef.current = /** @type {string} */ (data.account_id)
        const merged = coerceRow(data)
        const country = narrowCountry(merged.country)
        const tax_config = /** @type {Record<string, unknown>} */ (data).tax_config ?? null
        setBranding({
          ...DEFAULTS,
          ...merged,
          country,
          logo_url: merged.logo_url || '/logo.svg',
          tax_config,
        })
        if (merged.timezone) setTimezone(merged.timezone)
        setLocale(localeForCountry(country))
      }
    } catch (e) {
      // Fall back to defaults
    }
    setLoading(false)
  }

  /**
   * @param {Record<string, unknown>} updates
   */
  async function updateBranding(updates) {
    if (!accountIdRef.current) return { data: null, error: new Error('Branding not loaded') }
    // Upsert (not update) so a missing org_settings row is created lazily on first save.
    // Protects against accounts where handle_new_user's org_settings INSERT silently failed
    // (see Cure Run 17 D14/D15 — preview DB had pmowner account without an org_settings row).
    const { data, error } = await supabase
      .from('org_settings')
      .upsert(
        { account_id: accountIdRef.current, ...updates, updated_at: new Date().toISOString() },
        { onConflict: 'account_id' }
      )
      .select()
      .single()
    if (!error && data) {
      const merged = coerceRow(data)
      const country = narrowCountry(merged.country)
      const tax_config = /** @type {Record<string, unknown>} */ (data).tax_config ?? null
      setBranding(prev => ({ ...prev, ...merged, country, logo_url: merged.logo_url || '/logo.svg', tax_config }))
      if (merged.timezone) setTimezone(merged.timezone)
      setLocale(localeForCountry(country))
    }
    return { data, error }
  }

  return (
    <BrandingContext.Provider value={/** @type {BrandingContextValue} */ ({ ...branding, loading, updateBranding, reload: loadBranding })}>
      {children}
    </BrandingContext.Provider>
  )
}

/**
 * @returns {BrandingContextValue}
 */
export function useBranding() {
  return useContext(BrandingContext)
}
